import React, { useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, CalendarClock } from "lucide-react";

export default function InquiryDialog({ open, onClose, parcel }) {
  const [form, setForm] = useState({ full_name: "", country_code: "+91", phone: "", intent: "Buy", sub_type: "Land" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { whatsapp_url, calendar_url }

  const close = () => {
    onClose();
    setTimeout(() => { setResult(null); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) { toast.error("Please add your name and phone"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leads", {
        ...form,
        source: "property_inquiry",
        parcel_id: parcel?.id,
        status: "new",
      });
      setResult(data);
      toast.success("Brochure request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-md" data-testid="inquiry-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl tracking-tight">Request Detailed Brochure</DialogTitle>
              <p className="text-xs text-slate-400 mt-1">{parcel?.title}</p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Full name</Label>
                <Input data-testid="inquiry-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Phone</Label>
                <div className="mt-2 flex gap-2">
                  <Select value={form.country_code} onValueChange={(v) => setForm({ ...form, country_code: v })}>
                    <SelectTrigger className="bg-[#0F172A] border-[#334155] text-white h-11 w-24"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {["+91","+1","+44","+971","+65","+61"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid="inquiry-phone" required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })}
                    className="bg-[#0F172A] border-[#334155] text-white h-11 flex-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Intent</Label>
                  <Select value={form.intent} onValueChange={(v) => setForm({ ...form, intent: v })}>
                    <SelectTrigger className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {["Buy","Sell","Rent/Lease"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Category</Label>
                  <Select value={form.sub_type} onValueChange={(v) => setForm({ ...form, sub_type: v })}>
                    <SelectTrigger className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {["Land","Plot","Apartment","Villa"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <Button type="button" variant="outline" onClick={close}
                  className="flex-1 h-11 rounded-full border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white">
                  Cancel
                </Button>
                <Button data-testid="inquiry-submit" type="submit" disabled={busy}
                  className="flex-1 h-11 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                  {busy ? "Sending…" : "Request Brochure"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="inquiry-done">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#10B981]/15 grid place-items-center">
              <CheckCircle2 className="h-6 w-6 text-[#10B981]" />
            </div>
            <h2 className="mt-4 font-display text-xl font-semibold">Brochure on the way.</h2>
            <p className="mt-2 text-sm text-slate-400">Chat with us on WhatsApp for the fastest reply.</p>
            <div className="mt-6 flex flex-col gap-3">
              <a data-testid="inquiry-whatsapp" href={result.whatsapp_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-medium transition-colors">
                <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
              </a>
              <a href={result.calendar_url}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[#334155] text-white hover:bg-[#0F172A]">
                <CalendarClock className="h-4 w-4 text-[#10B981]" /> Book an advisor slot
              </a>
              <button onClick={close} className="mt-1 text-xs text-slate-500 hover:text-slate-300 uppercase tracking-[0.2em]">Back to parcel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
